'use client';

import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { SPORTS, type Sport } from '@rfqsports/shared';
import { useStore } from '@/lib/store';
import { cn } from '@/lib/cn';
import {
  parseSportFilter,
  SPORT_ICON,
  SPORT_LABEL,
  type SportKey,
} from '@/lib/sport';

type StatusKey = 'live' | 'all' | 'upcoming';

interface SidebarEntry {
  key: SportKey;
  label: string;
  icon: string;
}

interface Props {
  className?: string;
  // When set, the sidebar emits onClose() after each click — used by the mobile
  // drawer to dismiss itself once the user picks a filter.
  onClose?: () => void;
}

export function SportSidebar({ className, onClose }: Props): JSX.Element {
  const games = useStore((s) => s.games);
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  // Counts are derived from the live game list — match what the user will
  // actually see once they click the filter. Built dynamically over SPORTS so
  // a new sport added to the union shows up here with no further edits.
  const counts = useMemo(() => {
    const perSport = Object.fromEntries(SPORTS.map((s) => [s, 0])) as Record<Sport, number>;
    for (const g of games) {
      perSport[g.sport]++;
    }
    return { ...perSport, all: games.length };
  }, [games]);

  // Sport tabs auto-hide when their count drops to zero — the lobby follows
  // what the executor is actually serving, so flipping `ACTIVE_SPORTS_CSV`
  // on the executor takes off-season sports out of the sidebar without a
  // web redeploy. "All Sports" is always present.
  const sportEntries = useMemo<SidebarEntry[]>(() => {
    return [
      { key: 'all', label: 'All Sports', icon: '☰' },
      ...SPORTS.filter((s) => counts[s] > 0).map(
        (s): SidebarEntry => ({ key: s, label: SPORT_LABEL[s], icon: SPORT_ICON[s] }),
      ),
    ];
  }, [counts]);

  const sportFilter = parseSportFilter(params.get('sport')) ?? 'all';
  const statusFilter = parseStatus(params.get('status')) ?? 'upcoming';

  const setParam = (key: 'sport' | 'status', value: string | null): void => {
    const next = new URLSearchParams(params.toString());
    if (!value) next.delete(key);
    else next.set(key, value);
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    onClose?.();
  };

  return (
    <aside className={cn('flex flex-col gap-1 text-sm', className)} aria-label="Sport filter">
      <SidebarSection title={null}>
        <SidebarRow
          icon="●"
          iconClass="text-err"
          label="Live"
          active={statusFilter === 'live'}
          onClick={() => setParam('status', statusFilter === 'live' ? null : 'live')}
        />
        <SidebarRow
          icon="◷"
          iconClass="text-muted"
          label="Upcoming"
          active={statusFilter === 'upcoming'}
          onClick={() => setParam('status', null)}
        />
        <SidebarRow
          icon="∞"
          iconClass="text-muted"
          label="All"
          active={statusFilter === 'all'}
          onClick={() => setParam('status', 'all')}
        />
      </SidebarSection>

      <SidebarDivider />

      <SidebarSection title="Sports">
        {sportEntries.map((s) => (
          <SidebarRow
            key={s.key}
            icon={s.icon}
            iconClass="text-muted"
            label={s.label}
            count={counts[s.key]}
            active={sportFilter === s.key}
            onClick={() => setParam('sport', s.key === 'all' ? null : s.key)}
          />
        ))}
      </SidebarSection>
    </aside>
  );
}

function SidebarSection({
  title,
  children,
}: {
  title: string | null;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      {title ? (
        <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted">
          {title}
        </p>
      ) : null}
      {children}
    </div>
  );
}

function SidebarDivider(): JSX.Element {
  return <div className="my-2 h-px bg-border/70" aria-hidden />;
}

function SidebarRow({
  icon,
  iconClass,
  label,
  count,
  active,
  onClick,
}: {
  icon: string;
  iconClass?: string;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors',
        active
          ? 'bg-accent-strong/15 text-text font-semibold'
          : 'text-muted hover:bg-panel-2 hover:text-text',
      )}
    >
      <span className={cn('text-base leading-none', iconClass)} aria-hidden>
        {icon}
      </span>
      <span className="flex-1 text-sm">{label}</span>
      {count != null && count > 0 ? (
        <span className="text-[11px] text-muted tabular-nums">{count}</span>
      ) : null}
    </button>
  );
}

function parseStatus(v: string | null): StatusKey | null {
  if (v === 'live' || v === 'upcoming' || v === 'all') return v;
  return null;
}
